/**
 * PasswordInput - A molecule component for password input with visibility toggle.
 *
 * Combines the base Input component with a visibility toggle button.
 * Used in authentication forms (login, password reset).
 */

import { forwardRef, useState, type InputHTMLAttributes } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { Button } from '@lean-agility/design-system/react';
import { Label } from '@/components/atoms';
import { cn } from '@lean-agility/design-system/react';

export interface PasswordInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  /**
   * Label text displayed above the input.
   */
  label?: string;
  /**
   * Error message displayed below the input.
   */
  error?: string;
  /**
   * Hint text displayed below the input (when no error).
   */
  hint?: string;
  /**
   * Whether to show the password visibility toggle button.
   * @default true
   */
  showToggle?: boolean;
}

export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  ({ className, label, error, hint, showToggle = true, id, ...props }, ref) => {
    const [isVisible, setIsVisible] = useState(false);
    const inputId = id || label?.toLowerCase().replace(/\s+/g, '-');

    return (
      <div className="space-y-1.5">
        {label && <Label htmlFor={inputId}>{label}</Label>}
        <div className="relative">
          <input
            id={inputId}
            ref={ref}
            type={isVisible ? 'text' : 'password'}
            className={cn(
              'w-full rounded-lg border px-3 py-2 text-sm',
              `
                bg-transparent text-text-primary
                placeholder:text-text-muted
              `,
              `dark:text-text-primary`,
              'transition-colors duration-200',
              `focus:border-transparent focus:ring-2 focus:ring-primary-500 focus:outline-hidden`,
              error
                ? `
                  border-accent-rose
                  focus:ring-accent-rose
                `
                : `
                  border-border-strong
                  hover:border-border-input-hover
                  dark:border-border-strong
                  dark:hover:border-border-input-hover
                `,
              showToggle && 'pr-10',
              className,
            )}
            {...props}
          />
          {showToggle && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="absolute top-1/2 right-1 size-8 -translate-y-1/2"
              onClick={() => setIsVisible(!isVisible)}
              aria-label={isVisible ? 'Hide' : 'Show'}
            >
              {isVisible ? <EyeOff className="size-4 text-text-muted" /> : <Eye className="size-4 text-text-muted" />}
            </Button>
          )}
        </div>
        {error && <p className="text-xs text-feedback-error">{error}</p>}
        {hint && !error && <p className="text-xs text-text-muted">{hint}</p>}
      </div>
    );
  },
);

PasswordInput.displayName = 'PasswordInput';
